import { UserProfile } from '../../types';
import { profileDisplayName } from '../../utils/profileName';
import { TrainingSession } from '../../types/rowerJourney';

export interface ParentSession {
  id: string;
  name: string;
  date: string;
  /** ISO date (YYYY-MM-DD) for timeline heatmap */
  isoDate?: string;
  location: string;
  summary: string;
  value: string;
  boatName?: string;
  seatNumber?: number;
}

export interface ParentProgram {
  id: string;
  name: string;
  status: string;
  note: string;
  academicStatus: number;
  recruitmentStatus: number;
  notes: string;
}

export type ParentInboxItemKind =
  | 'parent_consent_request'
  | 'parent_reconsent_request'
  | 'guardian_invite'
  | 'notice';

export interface ParentAction {
  id: string;
  from: string;
  subject: string;
  date: string;
  status: string;
  kind?: ParentInboxItemKind;
  athleteId?: string;
  athleteName?: string;
  athleteEmail?: string;
  teamName?: string;
  inviterName?: string;
  message?: string;
  expiresAt?: string;
  consentToken?: string;
}

export type AthleteLinkStatus = 'linked' | 'pending' | 'unlinked';

export interface AthleteDeletionRequest {
  id: string;
  status: 'pending' | 'cancelled' | 'completed' | string;
  requestedAt: string;
  scheduledAt?: string | null;
  cancelledAt?: string | null;
  completedAt?: string | null;
}

/** A guardianship the parent recently revoked (parental consent withdrawn). */
export interface WithdrawnAthleteNotice {
  id: string;
  name: string;
  withdrawnAt: string;
  guardianshipId: string;
}

export interface LinkedAthlete {
  id: string;
  name: string;
  school: string;
  gradYear: number;
  linkStatus: AthleteLinkStatus;
  accountStatus?: string;
  avatarUrl?: string | null;
  /** Account deletion requests (scheduled + cancelled stay visible on athlete detail). */
  deletionRequests?: AthleteDeletionRequest[];
  sessions: ParentSession[];
  programs: ParentProgram[];
}

export interface ParentPortalData {
  parentName: string;
  firstName: string;
  lastName: string;
  dateOfBirth: string;
  parentEmail: string;
  phone: string;
  hometown: string;
  bio: string;
  relationship: string;
  athletes: LinkedAthlete[];
  actions: ParentAction[];
}

export const UNLINKED_ATHLETE_NAME = 'Athlete not linked';

export const titleCase = (value: string | null | undefined) => {
  if (!value) return '';
  return value
    .split(/[\s_-]+/)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
};

export const linkStatusLabel: Record<AthleteLinkStatus, string> = {
  linked: 'Linked',
  pending: 'Invite pending',
  unlinked: 'Not linked',
};

export const formatSeatLabel = (seatNumber?: number | null) => {
  if (!seatNumber || seatNumber < 1) return '—';
  return `Seat ${seatNumber}`;
};

export const getAthleteInitials = (name: string) => {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length >= 2) return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
  return (parts[0]?.slice(0, 2) || '?').toUpperCase();
};

export const previewPortalData: ParentPortalData = {
  parentName: 'Jon Thompson',
  firstName: 'Jon',
  lastName: 'Thompson',
  dateOfBirth: '1980-01-15',
  parentEmail: 'parent.preview@glyyd.com',
  phone: '(555) 123-4567',
  hometown: 'Marin, CA',
  bio: 'Interested in keeping recruiting, training, and consent activity organized in one place.',
  relationship: 'Parent',
  athletes: [
    {
      id: 'preview-avery',
      name: 'Avery Thompson',
      school: 'Marin Rowing Association',
      gradYear: 2027,
      linkStatus: 'linked',
      sessions: [
        {
          id: 'session-1',
          name: 'Morning steady state',
          date: 'Jun 18',
          isoDate: '2026-06-18',
          location: 'San Rafael',
          summary: '12.4km, 31 spm avg',
          value: '12.4km',
          boatName: 'Varsity 8+',
          seatNumber: 5,
        },
        {
          id: 'session-2',
          name: 'Race pace pieces',
          date: 'Jun 12',
          isoDate: '2026-06-12',
          location: 'Oakland Estuary',
          summary: '8.1km, 34 spm avg',
          value: '8.1km',
          boatName: 'Varsity 4-',
          seatNumber: 3,
        },
        {
          id: 'session-3',
          name: 'Technique row',
          date: 'Jun 04',
          isoDate: '2026-06-04',
          location: 'San Rafael',
          summary: '6.8km, 27 spm avg',
          value: '6.8km',
          boatName: 'Varsity 8+',
          seatNumber: 7,
        },
      ],
      programs: [
        {
          id: 'program-1',
          name: 'University of Washington',
          status: 'Pre-read',
          note: 'Academic materials queued for review.',
          academicStatus: 0,
          recruitmentStatus: 1,
          notes:
            'Transcript packet is ready. Coach follow-up should wait until the athlete confirms updated spring erg results.',
        },
        {
          id: 'program-2',
          name: 'University of California, Berkeley',
          status: 'Outreach',
          note: 'Coach contact saved.',
          academicStatus: 0,
          recruitmentStatus: 0,
          notes: 'Initial outreach saved. No parent action required.',
        },
      ],
    },
    {
      id: 'preview-jordan',
      name: 'Jordan Thompson',
      school: 'Marin Rowing Association',
      gradYear: 2029,
      linkStatus: 'pending',
      sessions: [],
      programs: [],
    },
  ],
  actions: [
    {
      id: 'action-1',
      kind: 'guardian_invite',
      from: 'Coach Sarah Chen',
      subject: 'Confirm guardian link for Jordan Thompson',
      date: 'Jun 20',
      status: 'Unread',
      athleteId: 'preview-jordan',
      athleteName: 'Jordan Thompson',
      teamName: 'Marin Rowing Association',
      inviterName: 'Coach Sarah Chen',
      message:
        'Sarah Chen invited you to connect as a guardian for Jordan Thompson. Accepting lets you view training activity, recruiting progress, and consent items for this athlete.',
      expiresAt: '2026-07-20T23:59:59.000Z',
    },
  ],
};

export const derivePortalData = (
  profile: UserProfile | null | undefined,
  email?: string
): ParentPortalData => {
  const parentName = profileDisplayName(profile);
  const firstName = profile?.first_name?.trim() || '';
  const lastName = profile?.last_name?.trim() || '';
  const dateOfBirth = profile?.date_of_birth || '';
  const childName = profile?.child_name?.trim();
  const hasChildHint = Boolean(childName);

  const athletes: LinkedAthlete[] = hasChildHint
    ? [
        {
          id: 'profile-child',
          name: childName!,
          school: profile?.child_high_school?.trim() || 'No school or club added yet',
          gradYear: profile?.child_grad_year || new Date().getFullYear() + 1,
          linkStatus: 'pending',
          sessions: [],
          programs: [],
        },
      ]
    : [];

  return {
    parentName,
    firstName,
    lastName,
    dateOfBirth,
    parentEmail: profile?.email || email || '',
    phone: profile?.phone || '',
    hometown: profile?.hometown || '',
    bio: profile?.bio || '',
    relationship: titleCase(profile?.relationship) || 'Parent',
    athletes,
    actions: [],
  };
};

export const getAthleteById = (
  data: ParentPortalData,
  athleteId?: string
): LinkedAthlete | null => {
  if (!athleteId) return data.athletes[0] ?? null;
  return data.athletes.find((athlete) => athlete.id === athleteId) ?? null;
};

export const getTotalDistance = (sessions: ParentSession[]) => {
  if (sessions.length === 0) return '--';
  const total = sessions.reduce((sum, session) => sum + parseFloat(session.value), 0);
  return `${total.toFixed(1)}km`;
};

export const parentSessionsToTrainingSessions = (sessions: ParentSession[]): TrainingSession[] =>
  sessions.map((session) => {
    const sessionDate = session.isoDate ? new Date(`${session.isoDate}T12:00:00`) : new Date();
    return {
      id: session.id,
      date: sessionDate,
      startTime: sessionDate,
      endTime: sessionDate,
      dataSource: 'peach' as const,
      sessionType: session.name,
      avgPower: 0,
      maxPower: 0,
      boatType: session.boatName || undefined,
      seatNumber: session.seatNumber,
      distance: parseFloat(session.value) || undefined,
    };
  });

export const parentSessionToSessionInfo = (
  session: ParentSession,
  teamName?: string
): import('../../services/telemetry/telemetryService').SessionInfo => ({
  id: session.id,
  sessionId: session.id,
  sessionName: session.name,
  sessionDate: session.isoDate || session.date,
  startTime: '',
  location: session.location,
  boatType: session.boatName || '',
  boat_name: session.boatName || null,
  seat_number: session.seatNumber ?? null,
  team_name: teamName || null,
  created_at: session.isoDate || session.date,
  roster_status: 'fully-mapped',
  roster_mapped: 8,
  roster_total: 8,
});

export const getProgramPipelineCounts = (programs: ParentProgram[]) => {
  const counts = [0, 0, 0, 0];
  programs.forEach((program) => {
    const stage = program.recruitmentStatus || 0;
    if (stage >= 0 && stage < 4) counts[stage]++;
  });
  return counts;
};
